<template>
  <section
    class="video-section"
    @mouseenter="videoPlaying = true"
    @mouseleave="videoPlaying = false"
  >
    <TvaBackgroundVideo
      class="video-section__video"
      :pathToVideo="videoPath"
      :playing="videoPlaying"
    />
    <RoundIconButton
      v-if="smallscreen"
      class="video-section__play-btn"
      :icon="play"
      @onClick="showModal(true)"
    />

    <div class="video-section__left">
      <MediaInfo
        :title="title"
        :mediaType="$t('general.labels.video')"
        :introText="introText"
        :icon="play"
        :smallscreen="smallscreen"
        @playMedia="showModal(true)"
      />
    </div>
    <div v-if="!smallscreen" class="video-section__right" />

    <VideoModal
      :show="modalShown"
      :videoPath="videoPath"
      @onClose="showModal(false)"
    />
  </section>
</template>

<script setup>
import { ref, computed, inject } from "vue";
import { useI18n } from "vue-i18n";
import useTvaMq from "@/plugins/tvaMq.js";
import { pushData } from "@/utilities/analytics-functions.js";

import TvaBackgroundVideo from "../general/TvaBackgroundVideo.vue";
import MediaInfo from "../general/MediaInfo.vue";
import VideoModal from "../general/VideoModal.vue";
import RoundIconButton from "../general/RoundIconButton.vue";

import { play } from "@/assets/icons/icons.js";

const props = defineProps({
  title: String,
  introText: String,
  videoFileName: String,
  currentPage: String,
  id: String,
});

const $baseUrl = inject("$baseUrl");
const i18n = useI18n();

const { $tvaMq } = useTvaMq();
const smallscreen = computed(() => ["tablet", "mobile"].includes($tvaMq.value));

const modalShown = ref(false);
const videoPlaying = ref(false);

const videoPath = computed(() => {
  const videoNr = props.id.split("-")[props.id.split("-").length - 1];
  return `${props.currentPage}_${i18n.locale.value}-${videoNr}`;
});

function showModal(value) {
  modalShown.value = value;
  pushData("video", `${$baseUrl}videos/${videoPath.value}.mp4`, props.title);
}
</script>

<style lang="scss" scoped>
.video-section {
  position: relative;
  overflow: hidden;

  .desktop &,
  .large & {
    height: 80vh;
    color: var(--white-100);
  }

  &__video {
    height: auto;
    aspect-ratio: 16/9;

    .desktop &,
    .large & {
      top: 50%;
      transform: translateY(-50%);
    }

    .mobile &,
    .tablet & {
      position: relative;
    }
  }

  &__play-btn {
    position: absolute;
    left: 50%;
    top: calc(100vw * 0.24);
    transform: translate(-50%, -50%);
  }

  &__left,
  &__right {
    height: 100%;
    background: var(--black-25);
    backdrop-filter: blur(10px);
  }

  &__left {
    display: flex;
    align-items: center;

    .desktop &,
    .large & {
      padding: 2rem 5vw 2rem var(--page-padding-h);
      width: calc(var(--left-space) + var(--page-padding-h));
    }

    .mobile &,
    .tablet & {
      width: calc(100% - var(--page-padding-h) * 2);
      background-color: var(--scala-grigi-gray-5);
      margin: -2rem var(--page-padding-h) 0;
      padding: var(--page-padding-h);
      border: var(--section-border);
      border-bottom: none;
    }
  }

  &__right {
    position: absolute;
    top: 0;
    right: 0;
    width: var(--page-padding-h);
  }
}
</style>
